import { useCallback, useEffect, useState } from 'react';
import { ImagePickerAsset, MediaTypeOptions } from 'expo-image-picker';

import { ImagePickerResult, useImagePicker } from '@pletly/common';
import { Toast } from '@pletly/ui';

import i18n from '../../i18n';
import { useProfilePicture } from './useProfilePicture';
import { getFaceDescriptorFor } from '../../services';

type ReferenceImageState =
	| {
			file?: undefined;
			faceDescriptor?: undefined;
			status: 'initial';
	  }
	| {
			file: ImagePickerResult | HTMLImageElement;
			faceDescriptor: Float32Array;
			status: 'success';
	  }
	| {
			file: ImagePickerResult | HTMLImageElement;
			faceDescriptor?: undefined;
			status: 'face-descriptor-error';
	  }
	| {
			file: ImagePickerResult | HTMLImageElement;
			faceDescriptor?: undefined;
			status: 'file-selected';
	  };

export const useReferenceImage = (defaultReferenceImageUrl?: string) => {
	const { t } = i18n;

	const [referenceImage, setReferenceImage] = useState<ReferenceImageState>({ status: 'initial' });
	const profilePicture = useProfilePicture(defaultReferenceImageUrl);

	const showNoFaceDetectedToast = useCallback(() => {
		Toast.show({
			type: 'warning',
			text1: t('no-face-detected'),
			text2: t('try-a-different-image'),
		});
	}, [t]);

	const setReferenceImageFaceDescriptor = useCallback(async () => {
		if (referenceImage.status !== 'file-selected') {
			return;
		}

		const { file } = referenceImage;
		let imgElementToProcess: HTMLImageElement | undefined;
		if (file instanceof HTMLImageElement) {
			imgElementToProcess = file;
		} else {
			imgElementToProcess = new Image();
			imgElementToProcess.src = file.uri;
		}
		const faceDescriptor = await getFaceDescriptorFor(imgElementToProcess);
		if (!faceDescriptor) {
			showNoFaceDetectedToast();

			setReferenceImage({
				status: 'face-descriptor-error',
				file,
				faceDescriptor: undefined,
			});

			return;
		}

		setReferenceImage({
			status: 'success',
			faceDescriptor,
			file,
		});

		return faceDescriptor;
	}, [referenceImage, showNoFaceDetectedToast]);

	const onReferenceImageSelect = useCallback((file: ImagePickerAsset | HTMLImageElement) => {
		setReferenceImage({ status: 'file-selected', file });
	}, []);

	const getReferenceImageFaceDescriptor = useCallback(() => {
		if (referenceImage.status === 'success') {
			return referenceImage.faceDescriptor;
		}

		if (referenceImage.status === 'face-descriptor-error') {
			showNoFaceDetectedToast();

			return;
		}

		return setReferenceImageFaceDescriptor();
	}, [referenceImage, setReferenceImageFaceDescriptor, showNoFaceDetectedToast]);

	const selectReferenceImage = useImagePicker(onReferenceImageSelect, {
		allowsMultipleSelection: false,
		mediaTypes: MediaTypeOptions.Images,
	});

	useEffect(() => {
		if (!profilePicture || referenceImage.status !== 'initial') {
			return;
		}

		void onReferenceImageSelect(profilePicture);
	}, [onReferenceImageSelect, profilePicture, referenceImage.status]);

	return {
		referenceImage,
		getReferenceImageFaceDescriptor,
		selectReferenceImage,
	};
};
